/**
 * Articles – X-style long-form composer: Drafts / Published tabs, Write button.
 * List items: Draft badge, "Last saved", 3-dot menu. Empty state matches X.
 */
import {useCallback, useMemo, useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {useSession} from '#/state/session'
import {
  useStudioArticlesQuery,
  useStudioDeleteArticle,
  useStudioUpdateArticle,
} from '#/state/queries/studio'
import type {StudioArticle} from '#/lib/api/studio'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {DotGrid_Stroke2_Corner0_Rounded as EllipsisIcon} from '#/components/icons/DotGrid'
import {Pencil_Stroke2_Corner0_Rounded as PencilIcon} from '#/components/icons/Pencil'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import * as Layout from '#/components/Layout'
import * as Menu from '#/components/Menu'
import * as Prompt from '#/components/Prompt'
import {Text} from '#/components/Typography'
import type {CommonNavigatorParams} from '#/lib/routes/types'
import {List} from '#/view/com/util/List'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Articles'>

type Tab = 'drafts' | 'published'

function lastSavedLabel(updatedAt: string, _: (s: string) => string): string {
  const updated = new Date(updatedAt).getTime()
  const now = Date.now()
  const diffSec = Math.floor((now - updated) / 1000)
  if (diffSec < 60) return _(msg`Last saved just now`)
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return _(msg`Last saved ${diffMin}m ago`)
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return _(msg`Last saved ${diffHr}h ago`)
  return new Date(updatedAt).toLocaleDateString()
}

function ArticleListItem({
  article,
  onPress,
  onDelete,
  onUnpublishAndEdit,
}: {
  article: StudioArticle
  onPress: () => void
  onDelete: (id: string) => void
  onUnpublishAndEdit: (id: string) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const deletePromptControl = Prompt.usePromptControl()
  const isDraft = article.status === 'draft'
  const lastSaved = lastSavedLabel(article.updated_at, _)
  const titleDisplay = article.title?.trim() || _(msg`(Needs title)`)

  return (
    <>
      <Pressable
        onPress={onPress}
        style={({pressed}) => [
          a.flex_row,
          a.align_start,
          a.justify_between,
          a.p_md,
          a.border_t,
          t.atoms.border_contrast_low,
          t.atoms.bg,
          pressed && t.atoms.bg_contrast_25,
        ]}
        accessibilityRole="button"
        accessibilityLabel={article.title || _(msg`Untitled article`)}>
        <View style={[a.flex_1, a.min_w_0]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm, a.flex_wrap]}>
            {isDraft && (
              <View
                style={[
                  a.rounded_full,
                  a.px_sm,
                  a.py_2xs,
                  {backgroundColor: t.palette.primary_500},
                ]}>
                <Text style={[a.text_xs, a.font_semi_bold, {color: '#fff'}]}>
                  <Trans>Draft</Trans>
                </Text>
              </View>
            )}
            <Text style={[a.text_xs, t.atoms.text_contrast_low]}>{lastSaved}</Text>
          </View>
          <Text
            style={[a.text_md, a.font_semi_bold, a.pt_xs, t.atoms.text]}
            numberOfLines={1}>
            {titleDisplay}
          </Text>
          <Text
            style={[a.text_sm, a.pt_xs, t.atoms.text_contrast_medium]}
            numberOfLines={2}>
            {article.content?.slice(0, 120) || ''}
            {article.content && article.content.length > 120 ? '…' : ''}
          </Text>
        </View>
        <Menu.Root>
          <Menu.Trigger label={_(msg`Article options`)}>
            {({props}) => (
              <Pressable
                {...props}
                onPress={e => {
                  if (e && 'stopPropagation' in e)
                    (e as React.MouseEvent).stopPropagation()
                  props.onPress?.(e as never)
                }}
                style={({pressed}) => [
                  a.rounded_full,
                  a.p_sm,
                  pressed && t.atoms.bg_contrast_25,
                ]}>
                <EllipsisIcon
                  width={20}
                  height={20}
                  style={{color: t.palette.contrast_500}}
                  aria-hidden
                />
              </Pressable>
            )}
          </Menu.Trigger>
          <Menu.Outer>
            <Menu.Group>
              {!isDraft && (
                <Menu.Item
                  label={_(msg`Edit Article`)}
                  onPress={() => onUnpublishAndEdit(article.id)}>
                  <Menu.ItemIcon icon={PencilIcon} />
                  <Menu.ItemText>
                    <Trans>Edit Article</Trans>
                  </Menu.ItemText>
                </Menu.Item>
              )}
              <Menu.Item
                label={_(msg`Delete`)}
                onPress={() => deletePromptControl.open()}>
                <Menu.ItemIcon icon={TrashIcon} />
                <Menu.ItemText>
                  <Trans>Delete</Trans>
                </Menu.ItemText>
              </Menu.Item>
            </Menu.Group>
          </Menu.Outer>
        </Menu.Root>
      </Pressable>
      <Prompt.Outer control={deletePromptControl}>
        <Prompt.TitleText>
          <Trans>Delete Article?</Trans>
        </Prompt.TitleText>
        <Prompt.DescriptionText>
          <Trans>This cannot be undone.</Trans>
        </Prompt.DescriptionText>
        <Prompt.Actions>
          <Prompt.Cancel />
          <Prompt.Action
            onPress={() => {
              deletePromptControl.close()
              onDelete(article.id)
            }}
            cta={_(msg`Delete`)}
            color="negative"
          />
        </Prompt.Actions>
      </Prompt.Outer>
    </>
  )
}

export function ArticlesScreen({navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const {currentAccount} = useSession()
  const [tab, setTab] = useState<Tab>('drafts')

  const {data: allArticles = [], isLoading} = useStudioArticlesQuery({
    ownerDid: currentAccount?.did ?? null,
  })

  const drafts = useMemo(
    () => allArticles.filter(a => a.status === 'draft'),
    [allArticles],
  )
  const published = useMemo(
    () => allArticles.filter(a => a.status === 'published'),
    [allArticles],
  )

  const articles = tab === 'drafts' ? drafts : published
  const isEmpty = articles.length === 0
  const deleteArticle = useStudioDeleteArticle()
  const updateArticle = useStudioUpdateArticle()

  const onWrite = useCallback(() => {
    navigation.navigate('ArticleEditor', {})
  }, [navigation])

  const onOpenArticle = useCallback(
    (article: StudioArticle) => {
      if (article.status === 'published') {
        navigation.navigate('Article', {articleId: article.id})
      } else {
        navigation.navigate('ArticleEditor', {articleId: article.id})
      }
    },
    [navigation],
  )

  const onDeleteArticle = useCallback(
    (id: string) => {
      deleteArticle.mutate(id)
    },
    [deleteArticle],
  )

  const onUnpublishAndEdit = useCallback(
    (id: string) => {
      updateArticle.mutate(
        {id, input: {status: 'draft'}},
        {
          onSuccess: () => {
            navigation.navigate('ArticleEditor', {articleId: id})
          },
        },
      )
    },
    [updateArticle, navigation],
  )

  const renderItem = useCallback(
    ({item}: {item: StudioArticle}) => (
      <ArticleListItem
        article={item}
        onPress={() => onOpenArticle(item)}
        onDelete={onDeleteArticle}
        onUnpublishAndEdit={onUnpublishAndEdit}
      />
    ),
    [onOpenArticle, onDeleteArticle, onUnpublishAndEdit],
  )

  if (!currentAccount) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Articles</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Sign in to write articles.</Trans>
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="articlesScreen">
      <Layout.Header.Outer>
        {gtMobile ? (
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Articles</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        ) : (
          <>
            <Layout.Header.MenuButton />
            <Layout.Header.Content>
              <Layout.Header.TitleText>
                <Trans>Articles</Trans>
              </Layout.Header.TitleText>
            </Layout.Header.Content>
          </>
        )}
        <Layout.Header.Slot>
          <Button
            label={_(msg`Write`)}
            color="primary"
            size="small"
            onPress={onWrite}>
            <ButtonIcon icon={PencilIcon} position="left" />
            <ButtonText>
              <Trans>Write</Trans>
            </ButtonText>
          </Button>
        </Layout.Header.Slot>
      </Layout.Header.Outer>

      <View
        style={[
          a.flex_1,
          web({maxWidth: 600, width: '100%', alignSelf: 'center'}),
        ]}>
        <View style={[a.flex_row, a.border_b, t.atoms.border_contrast_low]}>
          <Pressable
            onPress={() => setTab('drafts')}
          style={[
            a.px_lg,
            a.py_md,
            tab === 'drafts' && [a.border_b_2, {borderBottomColor: t.palette.primary_500}],
          ]}
          accessibilityRole="tab"
          accessibilityState={{selected: tab === 'drafts'}}
          accessibilityLabel={_(msg`Drafts`)}>
          <Text
            style={[
              a.text_md,
              tab === 'drafts' ? t.atoms.text : t.atoms.text_contrast_medium,
            ]}>
            <Trans>Drafts</Trans>
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setTab('published')}
          style={[
            a.px_lg,
            a.py_md,
            tab === 'published' && [a.border_b_2, {borderBottomColor: t.palette.primary_500}],
          ]}
          accessibilityRole="tab"
          accessibilityState={{selected: tab === 'published'}}
          accessibilityLabel={_(msg`Published`)}>
          <Text
            style={[
              a.text_md,
              tab === 'published' ? t.atoms.text : t.atoms.text_contrast_medium,
            ]}>
            <Trans>Published</Trans>
          </Text>
        </Pressable>
        </View>

      {isLoading ? (
        <Layout.Center>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Loading…</Trans>
          </Text>
        </Layout.Center>
      ) : isEmpty ? (
        <View
          style={[
            a.flex_1,
            a.p_xl,
            a.align_center,
            a.justify_center,
            gtMobile && [a.flex_row, a.gap_2xl],
          ]}>
          <View style={[gtMobile ? a.flex_1 : undefined, gtMobile && {maxWidth: 360}]}>
            <Text
              style={[a.text_xl, a.font_bold, a.pb_sm, t.atoms.text, !gtMobile && a.text_center]}
              selectable>
              {tab === 'drafts' ? (
                <Trans>Your drafts live here</Trans>
              ) : (
                <Trans>No published articles yet</Trans>
              )}
            </Text>
          </View>
          <View
            style={[
              a.align_center,
              a.justify_center,
              a.gap_md,
              gtMobile && a.flex_1,
            ]}>
            <Text
              style={[
                a.font_bold,
                a.text_center,
                t.atoms.text,
                gtMobile ? [a.text_3xl, a.pb_xs] : [a.text_xl, a.pb_sm],
              ]}
              selectable>
              <Trans>This is where you write</Trans>
            </Text>
            <Text
              style={[a.text_md, a.pb_lg, a.text_center, t.atoms.text_contrast_medium]}
              selectable>
              <Trans>Continue a draft or create a new Article</Trans>
            </Text>
            <Button label={_(msg`Write`)} color="primary" size="large" onPress={onWrite}>
              <ButtonText>
                <Trans>Write</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>
      ) : (
        <List
          data={articles}
          renderItem={renderItem as (info: {item: StudioArticle}) => React.ReactElement}
          keyExtractor={item => item.id}
          sideBorders={false}
        />
      )}
      </View>
    </Layout.Screen>
  )
}
